import React from "react";
import { Spin, Button, message, Row, Col, Alert, Switch, Divider } from "antd";
import { FormInstance } from "antd/lib/form/Form";
import { AxiosResponse } from "axios";
import debounce from "lodash.debounce";
import * as Sentry from "@sentry/browser";
import queryString from "query-string";
import BaseForm from "../components/Form/BaseForm";

import {
  APIError,
  Customer,
  FormFieldConfig,
  SelectOptionsInterface,
  User,
} from "../helpers/shared-interfaces";
import {
  convertCustomersToSelectOptions,
  request,
  responseHandler,
  generateDisplayErrorMessage,
  mapAPIErrorToFormError,
} from "../helpers/utilities";
import { SEARCH_CUSTOMER } from "../gql/customers";
import { CREATE_FIXED_INCOME_REQUEST } from "../gql/fixed-income-requests";
import { GET_PROVIDERS } from "../gql/loans";
import { USERS_BY_ROLE_QUERY, GET_CURRENT_USER } from "../gql/users";
import { ghanaCardRegex } from "../helpers/constants";
import { DEBOUNCE_TIME } from "../helpers/constants";


interface NewFixedIncomeProps {
  customer?: Customer;
  onSuccess?: () => void;
}

interface NewFixedIncomeState {
  isSubmitting: boolean;
  selectedProviderId?: string;
  selectedProvider?: any;
  selectedCustomer?: Customer;
  customers: Customer[];
  isSearching: boolean;
  searchTerm: string;
  providers: SelectOptionsInterface[];
  products: SelectOptionsInterface[];
  page: number;
  rawProviders: any[];
  createRequestError?: string;
  selectedProductId?: string;
  users: User[];
  selectedAssignedToId?: string;
  userSearchTerm: string;
  currentUser?: User;
  newCustomer: boolean;
  phoneNumbers: string[];
  emails: string[];
}

export class NewFixedIncomeRequest extends React.Component<
  NewFixedIncomeProps,
  NewFixedIncomeState
> {
  formRef = React.createRef<FormInstance>();

  state: NewFixedIncomeState = {
    isSubmitting: false,
    selectedProviderId: undefined,
    selectedCustomer: undefined,
    customers: [],
    isSearching: false,
    searchTerm: "",
    providers: [],
    products: [],
    page: 1,
    rawProviders: [],
    users: [],
    selectedAssignedToId: undefined,
    userSearchTerm: "",
    currentUser: undefined,
    newCustomer: false,
    phoneNumbers: [""],
    emails: [""],
  };

  convertUsersToSelectOptions = (users: User[]): SelectOptionsInterface[] => {
    return users.map((user) => ({
      id: user.id,
      name: user.name || user.email,
      value: user.id,
    }));
  };

  componentDidMount(): void {
    this.getAllProviders();
    this.getAdvisors();
    this.getCurrentUser();

    if (this.props.customer) {
      this.setState({
        selectedCustomer: this.props.customer,
        newCustomer: false,
      });
    } else {
      const parsed = queryString.parse(window.location.search);
      const mode = parsed.mode as string;

      if (mode === "new") {
        this.setState({ newCustomer: true });
      } else if (mode === "existing") {
        this.setState({ newCustomer: false });
      }
    }
  }

  componentDidUpdate(_: NewFixedIncomeProps, prevState: NewFixedIncomeState) {
    if (
      prevState.searchTerm !== this.state.searchTerm &&
      this.state.searchTerm
    ) {
      this.triggerCustomerSearch();
    }
  }

  getAllProviders(): void {
    const { page } = this.state;
    request({
      query: GET_PROVIDERS,
      variables: {
        page: page.toString(),
        perPage: "25",
        productCategory: "fixed_income",
      },
    })
      .then(({ data: { data } }: any): void => {
        const allProviders = data.getProviders || [];

        const providers = allProviders.filter(
          (provider: any) => provider.name === "PSL",
        );

        const pslProvider = providers.find((p: any) => p.name === "PSL");

        this.setState(
          {
            providers,
            rawProviders: providers,
            selectedProviderId: pslProvider?.id,
            selectedProvider: pslProvider,
            products: pslProvider?.activeProducts || [],
          },
          () => {
            if (pslProvider) {
              this.formRef.current?.setFieldsValue({
                providerId: pslProvider.id,
              });
            }
          },
        );
      })
      .catch((error) => {
        message.error("Failed to load providers");
        Sentry.captureException(error);
      });
  }

  getAdvisors = (): void => {
    request({
      query: USERS_BY_ROLE_QUERY,
      variables: {
        page: 1,
        q: this.state.userSearchTerm,
        role: "Advisors",
      },
    })
      .then(({ data: { data } }: any): void => {
        const users = data.users.results;

        const { currentUser } = this.state;
        if (currentUser && !users.find((u: User) => u.id === currentUser.id)) {
          users.unshift(currentUser);
        }

        this.setState({
          users: users,
        });
      })
      .catch((error) => {
        message.error("Failed to load users");
        Sentry.captureException(error);
      });
  };

  getCurrentUser = (): void => {
    request({
      query: GET_CURRENT_USER,
    })
      .then(({ data }: any): void => {
        const currentUser = data.data.currentUser;
        this.setState(
          {
            currentUser,
            selectedAssignedToId: currentUser.id,
          },
          () => {
            this.getAdvisors();

            this.formRef.current?.setFieldsValue({
              assignedToId: currentUser.id,
            });
          },
        );
      })
      .catch((error) => {
        message.error("Failed to load current user");
        Sentry.captureException(error);
      });
  };

  handleUserSearchInput = async (userSearchTerm: string): Promise<void> => {
    this.setState({ userSearchTerm }, () => {
      this.triggerUserDelayedSearch();
    });
  };

  triggerUserDelayedSearch = debounce(async (): Promise<void> => {
    this.getAdvisors();
  }, DEBOUNCE_TIME);

  triggerCustomerSearch = async (): Promise<void> => {
    const q = this.state.searchTerm;

    interface Response {
      count: number;
      results: Customer[];
    }

    this.setState({ isSearching: true });

    try {
      const r: AxiosResponse = await request({
        query: SEARCH_CUSTOMER,
        variables: { q, page: 1 },
      });

      const { err, mutation } = responseHandler<Response>(r, "customerSearch");

      if (err) {
        message.error(`Customer search failed - ${generateDisplayErrorMessage(err)}`);
      } else {
        this.setState({ customers: mutation.results });
      }
    } catch (error) {
      message.error(`Customer search failed - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    } finally {
      this.setState({ isSearching: false });
    }
  };

  handleCustomerSearch = debounce((searchTerm: string): void => {
    this.setState({ searchTerm });
  }, DEBOUNCE_TIME);

  handleCustomerSelect = (id: string | number): void => {
    const customerId = String(id);
    const selectedCustomer = this.state.customers.find(
      (c) => c.id === customerId,
    );

    this.setState({
      selectedCustomer,
    });

    this.formRef.current?.setFieldsValue({
      customerId: customerId,
    });
  };

  handleProviderSelect = (id: string | number): void => {
    const providerId = String(id);
    const { rawProviders } = this.state;
    const selectedProvider = rawProviders.find((p) => p.id === providerId);

    const products = selectedProvider?.activeProducts || [];

    this.setState({
      selectedProviderId: providerId,
      selectedProvider,
      products,
    });

    this.formRef.current?.setFieldsValue({
      productId: undefined,
    });
  };

  handleProductSelect = (id: string | number): void => {
    this.setState({
      selectedProductId: String(id),
    });
  };

  handleAssignedToSelect = (id: string | number): void => {
    this.setState({
      selectedAssignedToId: String(id),
    });
  };

  toggleNewCustomer = (checked: boolean): void => {
    this.setState({ newCustomer: checked });
    if (checked) {
      this.formRef.current?.setFieldsValue({ customerId: undefined });
    }
  };

  handlePhoneNumberChange = (index: number, value: string): void => {
    const newPhoneNumbers = [...this.state.phoneNumbers];
    newPhoneNumbers[index] = value;
    this.setState({ phoneNumbers: newPhoneNumbers });
  };

  addPhoneNumber = (): void => {
    this.setState({
      phoneNumbers: [...this.state.phoneNumbers, ""],
    });
  };

  removePhoneNumber = (index: number): void => {
    if (this.state.phoneNumbers.length > 1) {
      const newPhoneNumbers = this.state.phoneNumbers.filter(
        (_, i) => i !== index,
      );
      this.setState({ phoneNumbers: newPhoneNumbers });
    }
  };

  handleEmailChange = (index: number, value: string): void => {
    const newEmails = [...this.state.emails];
    newEmails[index] = value;
    this.setState({ emails: newEmails });
  };

  addEmail = (): void => {
    this.setState({
      emails: [...this.state.emails, ""],
    });
  };

  removeEmail = (index: number): void => {
    if (this.state.emails.length > 1) {
      const newEmails = this.state.emails.filter((_, i) => i !== index);
      this.setState({ emails: newEmails });
    }
  };

  redirectToViewRequestPage(id: string): void {
    window.location.href = `/fixed-income-requests/${id}`;
  }

  createFixedIncomeRequest = async (): Promise<void> => {
    if (!this.formRef.current) return;

    const { newCustomer } = this.state;

    let fieldsToValidate = ["providerId", "productId", "assignedToId"];

    if (newCustomer) {
      fieldsToValidate.push(
        "firstName",
        "gender",
        "lastName",
        "middleName",
        "dateOfBirth",
        "ghanaCardNo",
        "phoneNumbers",
        "emails",
      );
    } else {
      fieldsToValidate.push("customerId");
    }

    let data;

    try {
      data = await this.formRef.current.validateFields(fieldsToValidate);
    } catch (error) {
      console.error("Form validation error:", error);
      message.error(`Please fill in all required fields - ${generateDisplayErrorMessage(error)}`);
      return;
    }

    this.setState({ isSubmitting: true, createRequestError: undefined });

    try {
      const variables: any = {
        providerId: data.providerId,
        productId: data.productId,
        assignedToId: data.assignedToId,
        source: "phoenix",
        newCustomer,
      };

      if (newCustomer) {
        // Get phoneNumbers and emails from form data
        const formPhoneNumbers: string[] = data.phoneNumbers || [];
        const formEmails: string[] = data.emails || [];
        const validPhoneNumbers = formPhoneNumbers.filter(
          (p) => p && p.trim() !== "",
        );
        const validEmails = formEmails.filter((e) => e && e.trim() !== "");

        variables.firstName = data.firstName;
        variables.gender = data.gender;
        variables.lastName = data.lastName;
        variables.middleName = data.middleName;
        variables.dateOfBirth = data.dateOfBirth;
        variables.ghanaCardNo = data.ghanaCardNo;
        variables.phoneNumbers =
          validPhoneNumbers.length > 0 ? validPhoneNumbers : undefined;
        variables.emails = validEmails.length > 0 ? validEmails : undefined;
      } else {
        variables.customerId = this.props.customer?.id || data.customerId;
      }

      const response: AxiosResponse = await request({
        query: CREATE_FIXED_INCOME_REQUEST,
        variables,
      });

      const { mutation, err } = responseHandler<{
        fixedIncomeRequest?: any;
        createFixedIncomeRequest?: {
          fixedIncomeRequest: any;
          errors: APIError[];
        };
      }>(response, "createFixedIncomeRequest");

      const { fixedIncomeRequest } =
        mutation?.createFixedIncomeRequest ?? mutation;

      if (fixedIncomeRequest && !err) {
      message.success("Fixed income request created successfully!");
      this.redirectToViewRequestPage(fixedIncomeRequest.id);
      } else if (err || fixedIncomeRequest?.errors?.length) {
        const errors = err || fixedIncomeRequest.errors;
        console.error("Fixed income request errors:", errors);
        message.error(generateDisplayErrorMessage(errors));
        this.formRef.current?.setFields(errors.map(mapAPIErrorToFormError));
        this.setState({
          createRequestError: generateDisplayErrorMessage(errors),
        });
      } else {
        message.error("An unexpected error occurred.");
        this.setState({ createRequestError: "An unexpected error occurred." });
      }
    } catch (error) {
      console.error("Failed to create fixed income request:", error);
      message.error(`Failed to create fixed income request - ${generateDisplayErrorMessage(error)}`);
      this.setState({
        createRequestError: "Failed to create fixed income request",
      });
      Sentry.captureException(error);
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  render() {
    const {
      isSubmitting,
      customers,
      isSearching,
      providers,
      products,
      createRequestError,
      users,
      newCustomer,
      phoneNumbers,
      emails,
    } = this.state;

    const existingCustomerFields: FormFieldConfig[] = [
      {
        id: "customerId",
        name: "customerId",
        inputType: "search",
        required: true,
        placeholder:
          "Search by Email, Phone, SSNIT, First name, Middle name, Last name or PetraID ...",
        rule: "string",
        label: "Customer",
        disabled: !!this.props.customer,
        initialValue: this.props.customer?.id,
        selectOptions: convertCustomersToSelectOptions(customers),
        changeHandler: (val: string) => this.handleCustomerSearch(val),
        selectHandler: this.handleCustomerSelect,
        isSearching,
        colConfig: { span: 23 },
      },
    ];

    const newCustomerFields: FormFieldConfig[] = [
      {
        id: "firstName",
        name: "firstName",
        inputType: "string",
        rule: "string",
        required: true,
        placeholder: "Enter first name",
        label: "First Name",
        colConfig: { span: 11 },
      },
      {
        id: "lastName",
        name: "lastName",
        inputType: "string",
        rule: "string",
        required: true,
        placeholder: "Enter last name",
        label: "Last Name",
        colConfig: { span: 11, offset: 1 },
      },
      {
        id: "middleName",
        name: "middleName",
        inputType: "string",
        rule: "string",
        required: false,
        placeholder: "Enter middle name",
        label: "Middle Name",
        colConfig: { span: 11 },
      },
      {
        id: "gender",
        name: "gender",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select gender",
        label: "Gender",
        selectOptions: [
          { id: "male", name: "Male" },
          { id: "female", name: "Female" },
        ],
        colConfig: { span: 11, offset: 1 },
      },
      {
        id: "dateOfBirth",
        name: "dateOfBirth",
        inputType: "date",
        rule: "string",
        required: true,
        placeholder: "Select date of birth",
        label: "Date of Birth",
        colConfig: { span: 11 },
        dateOptions: {
          limitTo18: false,
          allowPastDates: true,
        },
      },
      {
        id: "ghanaCardNo",
        name: "ghanaCardNo",
        inputType: "string",
        rule: "string",
        required: false,
        placeholder: "Enter Ghana Card number",
        label: "Ghana Card Number",
        pattern: ghanaCardRegex,
        patternErrorMessage:
          "Invalid Ghana Card Number format. Expected format: ABC-123456789-1",
        colConfig: { span: 11, offset: 1 },
      },
      {
        id: "phoneNumbers",
        name: "phoneNumbers",
        inputType: "phoneArray",
        rule: "array",
        required: false,
        label: "Phone Numbers",
        placeholder: "Enter phone number",
        colConfig: { span: 11 },
        elementStyle: { width: "100%" },
        initialValue: phoneNumbers,
      },
      {
        id: "emails",
        name: "emails",
        inputType: "emailArray",
        rule: "array",
        required: true,
        label: "Email Addresses",
        placeholder: "Enter email address",
        colConfig: { span: 11, offset: 1 },
        elementStyle: { width: "100%" },
        initialValue: emails,
      },
    ];

    const commonFields: FormFieldConfig[] = [
      {
        id: "providerId",
        name: "providerId",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select Provider",
        label: "Provider",
        selectOptions: providers,
        colConfig: { span: 11 },
        selectHandler: this.handleProviderSelect,
        initialValue: this.state.selectedProviderId,
      },
      {
        id: "productId",
        name: "productId",
        inputType: "select",
        rule: "string",
        required: true,
        placeholder: "Select Product",
        label: "Product",
        selectOptions: products,
        selectHandler: this.handleProductSelect,
        colConfig: { span: 11, offset: 1 },
      },
      {
        id: "assignedToId",
        name: "assignedToId",
        inputType: "search",
        rule: "string",
        required: true,
        placeholder: "Search for user",
        label: "Assigned To",
        selectOptions: this.convertUsersToSelectOptions(users),
        changeHandler: this.handleUserSearchInput,
        selectHandler: this.handleAssignedToSelect,
        isSearching,
        colConfig: { span: 11 },
        initialValue: this.state.selectedAssignedToId,
      },
    ];

    const fields = [
      ...(newCustomer ? newCustomerFields : existingCustomerFields),
      ...commonFields,
    ];

    return (
      <Spin spinning={isSubmitting}>
        <Row>
          <Col span={12} style={{ margin: "0 20px" }}>
            {createRequestError && (
              <Alert
                message="Request Creation Error"
                description={createRequestError}
                type="error"
                showIcon
                closable
                style={{ marginBottom: 16 }}
                onClose={() => this.setState({ createRequestError: undefined })}
              />
            )}

            {!this.props.customer && (
              <>
                <div
                  style={{
                    marginBottom: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span style={{ fontWeight: 500 }}>Create New Customer:</span>
                  <Switch
                    checked={newCustomer}
                    onChange={this.toggleNewCustomer}
                  />
                </div>
                <Divider />
              </>
            )}

            <BaseForm
              layout="vertical"
              reference={this.formRef}
              fields={fields}
              fieldsToHide={[]}
            />

            <Row gutter={16} style={{ margin: "24px 0" }}>
              <Col span={23}>
                <Button
                  type="primary"
                  onClick={this.createFixedIncomeRequest}
                  loading={isSubmitting}
                  style={{ width: "100%" }}
                >
                  Create Fixed Income Request
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Spin>
    );
  }
}
